import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button } from "../../components/common/Primitives";
import ConfirmReasonDialog from "../../components/common/ConfirmReasonDialog";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { Clock, CheckCircle2, XCircle, User, Building2, Copy, Check } from "lucide-react";

const StatusPill = ({ s }) => {
  if (s === "pending_super_admin") return <Pill tone="warn">Pending</Pill>;
  if (s === "approved") return <Pill tone="success">Approved</Pill>;
  if (s === "rejected") return <Pill tone="danger">Rejected</Pill>;
  return <Pill tone="muted">{s}</Pill>;
};

/**
 * AdminOnboardingRequests — super_admin approval queue for startup/investor
 * onboarding requests submitted by admin/manager/agent. Non-super admins
 * see only their own requests (read-only) so they can track status.
 */
export default function AdminOnboardingRequests() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending_super_admin");
  const [tempResults, setTempResults] = useState({}); // request_id -> {temp_password, user_id}
  const [copiedFor, setCopiedFor] = useState(null);
  const [approveFor, setApproveFor] = useState(null); // request to approve (opens confirm dialog)
  const [rejectFor, setRejectFor] = useState(null); // request to reject

  const isSuperAdmin = user?.role === "super_admin";

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/onboarding-requests?status=${statusFilter}`);
      setRows(data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const approve = (req) => setApproveFor(req);
  const confirmApprove = async () => {
    const req = approveFor;
    if (!req) return;
    setBusyId(req.request_id);
    try {
      const { data } = await api.post(`/admin/onboarding-requests/${req.request_id}/approve`);
      const result = data?.result || {};
      if (result.temp_password) {
        setTempResults((m) => ({ ...m, [req.request_id]: {
          temp_password: result.temp_password, user_id: result.user_id,
        }}));
        toast.success("Approved. Copy the temporary password now.");
      } else {
        toast.success("Approved. Invitation email sent.");
      }
      setApproveFor(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const reject = (req) => setRejectFor(req);
  const confirmReject = async ({ reason }) => {
    const req = rejectFor;
    if (!req) return;
    setBusyId(req.request_id);
    try {
      await api.post(`/admin/onboarding-requests/${req.request_id}/reject`, { reason });
      toast.success("Rejected. Requester notified.");
      setRejectFor(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const copyPw = (req_id, pw) => {
    navigator.clipboard.writeText(pw).then(() => {
      setCopiedFor(req_id);
      setTimeout(() => setCopiedFor(null), 1500);
    });
  };

  return (
    <DashboardShell
      title="Onboarding Requests"
      subtitle="ADMIN · APPROVAL QUEUE"
    >
      <Toaster richColors position="top-right" />
      <ConfirmReasonDialog
        open={!!approveFor}
        title={`Approve ${approveFor?.payload?.email || "this request"}?`}
        description={`This immediately creates the ${approveFor?.kind || "user"} account and ${approveFor?.payload?.delivery_method === "invite" ? "emails an invitation link" : "returns a temporary password"}.`}
        confirmLabel="Approve & Create"
        tone="emerald"
        reasonRequired={false}
        reasonLabel="Internal note (optional — for audit log)"
        reasonPlaceholder=""
        onConfirm={confirmApprove}
        onClose={() => setApproveFor(null)}
        testId="onboarding-approve-dialog"
      />
      <ConfirmReasonDialog
        open={!!rejectFor}
        title={`Reject ${rejectFor?.payload?.email || "this request"}?`}
        description="The requester (admin/manager/agent) will be notified with this reason. The user account will not be created."
        confirmLabel="Reject Request"
        tone="danger"
        reasonPlaceholder="e.g. Duplicate — this founder already has an account."
        onConfirm={confirmReject}
        onClose={() => setRejectFor(null)}
        testId="onboarding-reject-dialog"
      />
      <div className="flex items-center gap-2 mb-4">
        {["pending_super_admin", "approved", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-md text-xs font-heading border ${
              statusFilter === s
                ? "bg-emerald-700 text-white border-emerald-700"
                : "bg-white text-ink border-navy/15 hover:border-emerald-500"
            }`}
            data-testid={`filter-${s}`}
          >
            {s === "pending_super_admin" ? "Pending" : s === "approved" ? "Approved" : "Rejected"}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <Card>
          <div className="text-sm text-ink-muted text-center py-6" data-testid="onboarding-empty">
            No {statusFilter.replace("_super_admin", "")} onboarding requests.
          </div>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="onboarding-requests-list">
          {rows.map((r) => {
            const p = r.payload || {};
            const isStartup = r.kind === "startup";
            const temp = tempResults[r.request_id];
            return (
              <Card key={r.request_id} testId={`onboarding-request-${r.request_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-800 flex items-center gap-1">
                        {isStartup ? <Building2 size={11} /> : <User size={11} />}
                        {r.kind}
                      </div>
                      <StatusPill s={r.status} />
                      <span className="text-[11px] font-mono text-ink-muted flex items-center gap-1">
                        <Clock size={11} /> {(r.requested_at || "").slice(0, 16)}
                      </span>
                    </div>
                    <div className="font-heading text-ink text-lg mt-1">
                      {p.name} <span className="text-ink-muted">·</span> <span className="font-mono text-sm text-ink-soft">{p.email}</span>
                    </div>
                    <div className="text-xs text-ink-muted mt-1">
                      Requested by <b>{r.requested_by}</b> ({r.requested_by_role})
                      {p.mobile && ` · ${p.mobile}`}
                      {p.delivery_method && ` · Delivery: ${p.delivery_method === "invite" ? "Invitation email" : "Temporary password"}`}
                    </div>
                    {isStartup && (p.startup_name || p.sector || p.stage || p.funding_ask_inr) && (
                      <div className="text-xs text-ink-soft mt-2 font-mono">
                        {p.startup_name && `Startup: ${p.startup_name} · `}
                        {p.sector && `Sector: ${p.sector} · `}
                        {p.stage && `Stage: ${p.stage} · `}
                        {p.funding_ask_inr && `Ask: ₹${(p.funding_ask_inr / 1e7).toFixed(2)}Cr`}
                      </div>
                    )}
                    {!isStartup && (p.investor_type || p.ticket_size_inr) && (
                      <div className="text-xs text-ink-soft mt-2 font-mono">
                        {p.investor_type && `Type: ${p.investor_type} · `}
                        {p.ticket_size_inr && `Ticket: ₹${(p.ticket_size_inr / 1e7).toFixed(2)}Cr`}
                      </div>
                    )}
                    {p.note && (
                      <div className="text-xs text-ink-muted mt-2 italic bg-navy/[0.03] rounded px-2 py-1.5">
                        Note: {p.note}
                      </div>
                    )}
                    {r.status === "rejected" && r.reject_reason && (
                      <div className="text-xs text-rose-700 mt-2 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
                        Rejected by {r.rejected_by}: {r.reject_reason}
                      </div>
                    )}
                    {r.status === "approved" && r.approved_by && (
                      <div className="text-xs text-emerald-700 mt-2">
                        Approved by {r.approved_by} on {(r.approved_at || "").slice(0, 16)}
                        {r.created_user_id && ` · user_id: ${r.created_user_id}`}
                      </div>
                    )}
                    {temp && (
                      <div className="mt-3 flex items-center gap-2 bg-amber-50 border border-amber-300 rounded px-3 py-2">
                        <code className="flex-1 font-mono text-sm">{temp.temp_password}</code>
                        <button onClick={() => copyPw(r.request_id, temp.temp_password)}>
                          {copiedFor === r.request_id ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                      </div>
                    )}
                  </div>
                  {isSuperAdmin && !user?.demo_account && r.status === "pending_super_admin" && (
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        disabled={busyId === r.request_id}
                        onClick={() => approve(r)}
                        data-testid={`approve-request-${r.request_id}`}
                      >
                        <CheckCircle2 size={13} className="inline mr-1" /> Approve
                      </Button>
                      <Button
                        variant="danger"
                        disabled={busyId === r.request_id}
                        onClick={() => reject(r)}
                        data-testid={`reject-request-${r.request_id}`}
                      >
                        <XCircle size={13} className="inline mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                  {isSuperAdmin && user?.demo_account && r.status === "pending_super_admin" && (
                    <div className="text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      Demo mode · read-only
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </DashboardShell>
  );
}
